import {DOCUMENT} from '@angular/common';
import {Component, HostListener, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, UntypedFormControl, Validators} from '@angular/forms';
import {MatSidenav} from '@angular/material/sidenav';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {intComparator} from '@application/helper/int-comparator';
import {isClickOutsideGivenElements} from '@application/helper/is-click-outside-given-elements';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationFunctionData} from '@application/helper/navigation-helper/navigation-function-data.interface';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {NavigationSelectedMenuItemData} from '@application/helper/navigation-helper/navigation-selected-menu-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {MachineType} from '@domain/machine/machine-type.enum';
import {ProductConfiguration} from '@domain/product-configuration/product-configuration';
import {VersionHistory} from '@domain/product-configuration/version-history';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {ColoredYarnSet} from '@domain/textile-data/colored-yarn-set/colored-yarn-set';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {IdNameVersionPosition} from '@domain/textile-data/weave-structure/id-name-version-position';
import {WeaveStructure} from '@domain/textile-data/weave-structure/weave-structure';
import {WeaveStructureForMachineQuality} from '@domain/textile-data/weave-structure/weave-structure-for-machine-quality';
import {WeftColoredYarnSet} from '@domain/textile-data/weft-colored-yarn-set/weft-colored-yarn-set';
import {CadCamService} from '@infrastructure/cad-cam/cad-cam.service';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {COLORED_YARN_SETS, ColoredYarnSets} from '@infrastructure/http/colored-yarn-set/colored-yarn-sets';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {GetAllMachineQualitiesParameters, MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {PRODUCT_CONFIGURATIONS, ProductConfigurations} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {Profile, PROFILE} from '@infrastructure/http/profile/profile';
import {WEFT_COLORED_YARN_SETS, WeftColoredYarnSets} from '@infrastructure/http/weft-colored-yarn-set/weft-colored-yarn-sets';
import {GridProductionStatusComponent} from '@presentation/components/grid-production-status/grid-production-status.component';
import {DesignPreviewComponent} from '@presentation/components/grid/design-preview/design-preview.component';
import {SelectMachineQualityComponent} from '@presentation/components/select-machine-quality/select-machine-quality.component';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {NavigationMachineOverviewData} from '@presentation/pages/machine-overview/navigation-machine-overview-data.interface';
import {SelectMachineComponent} from '@presentation/pages/texfab/production-schedule/add/initial-preparations-new/select-machine/select-machine.component';
import {OverviewListColoredYarnSet} from '@presentation/pages/textile-data/colored-yarn-set/overview/overview-list-colored-yarn-set';
import {SelectColoredYarnSetComponent} from '@presentation/pages/textile-data/creel/add/select-colored-yarn-set/select-colored-yarn-set.component';
import {WeaveStructureWidth} from '@presentation/pages/textile-data/machine-quality/add/machine-quality-general/weave-structure-width.enum';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {SelectWeaveStructureComponent} from '@presentation/pages/textile-data/machine-quality/select-weave-structure/select-weave-structure.component';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {
  AgGridRowSelection,
  AgGridUtils,
  AssertionUtils,
  BackendError,
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  DialogComponentData,
  DialogType,
  FormValidationHelper,
  GridModel,
  GridOptionsBuilderFactoryService,
  LoadingCellOverlayComponent,
  NoDataOverlayComponentParams,
  ObjectUtils,
  OverlayComponentParams,
  SaveType,
  skeletonViewAnimation,
  StringUtils,
  TabsMenuItem,
  ToastService,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  RowClickedEvent,
  RowNode,
  RowSelectedEvent,
  RowSelectionOptions,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {isEmpty, isNil} from 'lodash-es';
import {Observable} from 'rxjs';
import {finalize, map, takeUntil} from 'rxjs/operators';
import {AddProductConfigurationViewMode} from './add-product-configuration-view-mode';
import {NavigationAddProductConfigurationData} from './navigation-add-product-configuration-data.type';
import {NavigationProductConfigurationData} from './navigation-product-configuration-data.interface';
import {AdditionalOptionsForm, GeneralForm, ProductConfigurationForm} from './product-configuration.form';
import {SelectWeftColoredYarnSetComponent} from './select-weft-colored-yarn-set/select-weft-colored-yarn-set.component';

@Component({
  templateUrl: './add-product-configuration-page.component.html',
  styleUrls: ['./add-product-configuration-page.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .mat-card-header, .button-skeleton-wrapper, .mat-expansion-panel, .checkbox-skeleton, .text-skeleton-wrapper, .thread-item header')]
})
export class AddProductConfigurationPageComponent extends BaseComponent implements OnInit {
  @ViewChild('sidenav', {static: true}) public sidenav: MatSidenav;
  @ViewChild('designsGrid') public designsGrid: AgGridAngular;
  @ViewChild('selectedDesignsGrid') public selectedDesignsGrid: AgGridAngular;
  @ViewChild('versionsHistoryGrid') public versionsHistoryGrid: AgGridAngular;

  public productConfigurationLocked = false;
  public productConfigurationUnsavedChanges = false;
  public addProductConfigurationForm: ProductConfigurationForm;
  public additionalInformation = new FormControl<string>('');
  public listOfFilteredRawMaterials: Observable<string[]>;
  public translationKeyForProductConfiguration = 'PRODUCT_CONFIGURATION.PRODUCT_CONFIGURATION';
  public selectedMenuItem: TabsMenuItem;
  public generalMenuItem: TabsMenuItem = {
    value: AddProductConfigurationViewMode.GENERAL,
    translationKey: 'GENERAL.GENERAL'
  };

  public listOfVersionHistories: VersionHistory[];
  public gridOptionsForListOfVersionHistories: GridOptions;
  public listOfDesignsToBeShown: Drawing[];
  public gridOptionsForListOfDesigns: GridOptions;
  public gridOptionsForListOfSelectedDesigns: GridOptions;
  public showSelected = false;
  public loadingForTheFirstTime = true;
  public selectedDesigns: Drawing[] = [];
  public menuItems: TabsMenuItem[];
  public suggestionForProductConfigurationName = null;
  public customMessageTranslationKeys;
  public readonly texfabProductionSchedulePermission: Permission = Permission.TEXFAB_SCHEDULE;
  public readonly texeditCadCamProductConfigurationPermission: Permission = Permission.TEXEDIT_CADCAM_WEAVESTRUCTURE_EDIT;
  public readonly textileDataType = TextileDataType;
  public readonly QUALITY_CHIP_VALUE_KEY = '%(technicalName) (v%(version))';
  public readonly MACHINE_BASE_ROUTER_LINK = RouteUtils.paths.texState.editMachine.absolutePath;
  public readonly QUALITY_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath;
  public readonly WEAVE_STRUCTURE_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.weaveStructure.editWeaveStructure.absolutePath;
  public readonly COLORED_YARN_SET_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.coloredYarnSet.editColoredYarnSet.absolutePath;
  public readonly WEFT_COLORED_YARN_SET_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.weftColoredYarnSet.editWeftColoredYarnSet.absolutePath;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_PRODUCT_CONFIGURATION_ORDER;
  public readonly SAVE = SaveType.SAVE;
  public readonly SAVE_AND_CLOSE = SaveType.SAVE_AND_CLOSE;
  public readonly SAVE_AND_CREATE_NEW = SaveType.SAVE_AND_CREATE_NEW;

  private readonly PRODUCT_CONFIGURATION_OVERVIEW_URL = RouteUtils.paths.texEdit.productConfiguration.absolutePath;
  private readonly EDIT_PRODUCT_CONFIGURATION_URL = RouteUtils.paths.texEdit.editProductConfiguration.path;

  private selectedVersionHistory: VersionHistory;
  private saveButtonTouched = false;
  private disableMachine = false;
  private readonly listOfClassesThatShouldNotCloseSidebarOnClick = ['sidebar', 'cdk-overlay-container', 'ag-row', 'remove-button'];
  private readonly editObjectTitle = 'GENERAL.ACTIONS.EDIT_OBJECT';
  private currentSubscription: Subscription;
  private readonly QUANTITY_OF_DRAWINGS_TO_REQUEST_PER_SLICE = 20;
  private optionalSettingsMenuItem: TabsMenuItem = {
    value: AddProductConfigurationViewMode.OPTIONAL_SETTINGS,
    translationKey: 'GENERAL.OPTIONAL_SETTINGS'
  };

  private readonly dialogBuilderForBasicDialog = this.dialogBuilderFactoryService.getBuilder().withClass('basic-dialog-panel');

  public constructor(
    @Inject(PRODUCT_CONFIGURATIONS) private readonly productConfigurations: ProductConfigurations,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PROFILE) private readonly profile: Profile,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(MACHINE_QUALITIES) private readonly qualities: MachineQualities,
    @Inject(COLORED_YARN_SETS) public coloredYarnSets: ColoredYarnSets,
    @Inject(WEFT_COLORED_YARN_SETS) private weftColoredYarnSets: WeftColoredYarnSets,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly cadCamService: CadCamService,
    private readonly navigationHelperService: NavigationHelperService<NavigationAddProductConfigurationData>
  ) {
    super();
  }

  public get generalForm(): GeneralForm {
    return this.addProductConfigurationForm.controls.generalForm;
  }

  public get additionalOptionsForm(): AdditionalOptionsForm {
    return this.addProductConfigurationForm.controls.additionalOptionsForm;
  }

  @HostListener('document:click', ['$event'])
  public onOutsideDetailClick(event: MouseEvent): void {
    const targetElement: HTMLElement = event.target as HTMLElement;

    if (this.sidenav?.opened && isClickOutsideGivenElements(targetElement, this.listOfClassesThatShouldNotCloseSidebarOnClick)) {
      this.closeSelectedVersionHistoryDetails();
    }
  }

  public ngOnInit(): void {
    const urlParams: Params = this.activatedRoute.snapshot.params;

    this.selectedMenuItem = this.generalMenuItem;
    this.menuItems = [this.generalMenuItem, this.optionalSettingsMenuItem];
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.setProductConfigurationForm();

    const emptyProductConfigurationState = {productConfiguration: null, disableMachine: null} as NavigationProductConfigurationData;
    const productConfigurationState = this.navigationHelperService.getPartialState<NavigationProductConfigurationData>(Object.keys(emptyProductConfigurationState));
    if (!AssertionUtils.isNullOrUndefined(productConfigurationState)) {
      this.initialiseProductConfiguration(productConfigurationState.productConfiguration);
      this.disableMachine = productConfigurationState.disableMachine;
      if (this.disableMachine) {
        this.generalForm.controls.machine.disable();
      }
    } else if (!AssertionUtils.isNullOrUndefined(urlParams.id)) {
      this.productConfigurations
        .getById(Number(urlParams.id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: (productConfiguration: ProductConfiguration) => this.initialiseProductConfiguration(productConfiguration),
          error: ErrorHandlers.navigateToOverviewAndThrowError(this.router, this.PRODUCT_CONFIGURATION_OVERVIEW_URL, this.navigationHelperService)
        });
    } else {
      this.setMachine();
      this.initialiseDesignGridOptions();
    }

    const getFunctionsBasedOnDialogComponent = new Map<any, NavigationFunctionData>([
      [SelectMachineQualityComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewQuality(newItemId), reopenDialog: (): void => this.selectQuality()}],
      [SelectColoredYarnSetComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewColoredYarnSet(newItemId), reopenDialog: (): void => this.selectColoredYarnSet()}],
      [
        SelectWeftColoredYarnSetComponent,
        {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setNewWeftColoredYarnSet(newItemId), reopenDialog: (): void => this.selectWeftColoredYarnSet()}
      ]
    ]);

    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData, {dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).loadNewItem({newItemId}),
      ({dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).reopenDialog()
    );
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationAddProductConfigurationData>({
      productConfiguration: this.getCurrentProductConfiguration(),
      menuItem: this.selectedMenuItem,
      disableMachine: this.disableMachine
    });
  }

  public openCadCam(target: HTMLElement): void {
    this.cadCamService.openCadCam(target, this.getExternalApplicationLink(), this.unSubscribeOnViewDestroy);
  }

  public getTabAction(): string {
    const title = this.isEditingProductConfiguration() ? this.editObjectTitle : 'GENERAL.ACTIONS.CREATE_OBJECT';
    return this.translate.instant(title, {object: this.translate.instant(this.translationKeyForProductConfiguration, {count: 1}).toLowerCase()});
  }

  public getWeaveStructureName(): string {
    return !this.getPropertyValue('quality') ? null : `${this.getPropertyValue('quality').singleWidth.weaveStructure.name} (v${this.getPropertyValue('quality').singleWidth.weaveStructure.version})`;
  }

  public unlockProductConfiguration(): void {
    this.productConfigurations
      .updateLocked(this.activatedRoute.snapshot.params.id, false)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.productConfigurationLocked = false;
        this.addProductConfigurationForm.enable({emitEvent: false});
        const selectionOptions = {
          enableClickSelection: !this.productConfigurationLocked,
          checkboxes: !this.productConfigurationLocked,
          mode: AgGridRowSelection.MULTIPLE,
          headerCheckbox: !this.productConfigurationLocked
        } as RowSelectionOptions;
        this.gridOptionsForListOfDesigns.rowSelection = selectionOptions;
        this.gridOptionsForListOfSelectedDesigns.rowSelection = selectionOptions;
      });
  }

  public selectMachine(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectMachineComponent, {
        machineQualityId: this.getPropertyValue('quality')?.id,
        allowedMachineTypes: [MachineType.CARPET_LOOM, MachineType.CARPET_LOOM_NO_JQ, MachineType.VELVET]
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedMachine: MachineOverviewList) => {
        if (!!selectedMachine) {
          this.generalForm.controls.machine.setValue(selectedMachine);
          this.getSuggestionForProductConfigurationName();
        }
      });
  }

  public selectQuality(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectMachineQualityComponent, {
        title: this.translate.instant('GENERAL.ACTIONS.SELECT_OBJECT', {object: this.translate.instant('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY', {count: 1}).toLowerCase()}),
        selectedQualities: AssertionUtils.isNullOrUndefined(this.getPropertyValue('quality')) ? [] : [this.getPropertyValue('quality')],
        rowSelection: AgGridRowSelection.SINGLE,
        allowEmptySelection: false,
        machineId: this.getPropertyValue('machine')?.id,
        filterItems: (overview: OverviewListMachineQuality[]) => overview.filter((quality: OverviewListMachineQuality) => quality.weaveStructureWidth != WeaveStructureWidth.DOUBLE_WIDTH)
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((machineQualities: OverviewListMachineQuality[]) => {
        if (machineQualities?.length) {
          this.setQuality(machineQualities[0]);
        }
      });
  }

  private removeFromHelpWeaveStructures(weaveStructure: IdNameVersionPosition): void {
    const helpWeaveStructures = this.additionalOptionsForm.value.helpWeaveStructures;
    if (!AssertionUtils.isNullOrUndefined(helpWeaveStructures)) {
      this.additionalOptionsForm.controls.helpWeaveStructures.setValue(helpWeaveStructures.filter((helpWeaveStructure: WeaveStructure) => helpWeaveStructure.name !== weaveStructure.name));
    }
  }

  public selectHelpWeaveStructures(): void {
    const weaveStructureFromQuality = this.getPropertyValue('quality')?.singleWidth?.weaveStructure?.name;
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectWeaveStructureComponent, {
        selectMultiple: AgGridRowSelection.MULTIPLE,
        weaveStructures: this.additionalOptionsForm.value.helpWeaveStructures,
        objectTranslationKey: 'PRODUCT_CONFIGURATION.HELP_WEAVE_STRUCTURE',
        filterItems: (wvs: WeaveStructureForMachineQuality[]) =>
          wvs.filter((w: WeaveStructureForMachineQuality) => (!AssertionUtils.isNullOrUndefined(weaveStructureFromQuality) ? w.name !== weaveStructureFromQuality && !w.name.includes(',') : w))
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((weaveStructures: WeaveStructure[]) => {
        if (weaveStructures != null) {
          this.additionalOptionsForm.controls.helpWeaveStructures.setValue(weaveStructures);
        }
      });
  }

  public selectWeftColoredYarnSet(): void {
    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectWeftColoredYarnSetComponent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((coloredYarnSet: WeftColoredYarnSet) => {
        if (!isEmpty(coloredYarnSet)) {
          this.additionalOptionsForm.controls.weftColoredYarnSet.setValue(coloredYarnSet);
        }
      });
  }

  public selectColoredYarnSet(): void {
    const numberOfCreelPositions = this.getPropertyValue('quality')?.singleWidth?.weaveStructure?.numberOfCreelPositions;

    this.dialogBuilderForBasicDialog
      .openSelectGridDialog(SelectColoredYarnSetComponent, {numberOfCreelPositions})
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((coloredYarnSet: ColoredYarnSet) => {
        if (!isEmpty(coloredYarnSet)) {
          this.additionalOptionsForm.controls.coloredYarnSet.setValue(coloredYarnSet);
        }
      });
  }

  public getLabelFromArray(listOfWeaveStructures: WeaveStructure[]): string {
    return listOfWeaveStructures?.length > 0 ? listOfWeaveStructures.map((weaveStructure: WeaveStructure) => weaveStructure.name).join(', ') : null;
  }

  public onShowSelectedToggleChange(showSelected: boolean): void {
    if (!showSelected) {
      this.loadingForTheFirstTime = true;
    }
    this.showSelected = showSelected;
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.PRODUCT_CONFIGURATION_OVERVIEW_URL);
  }

  public navigateToCustomSettings(): void {
    this.router.navigateByUrl(RouteUtils.paths.texEdit.productConfigurationSettings.absolutePath);
  }

  public canShowCustomSettingsButton(): boolean {
    const requiredPermission = RouteUtils.paths.texEdit.productConfigurationSettings.requiredPermission;
    return this.currentSubscription?.hasPermission(requiredPermission);
  }

  public save(saveType: SaveType): void {
    this.saveButtonTouched = true;
    const isValid = new FormValidationHelper().checkForms([this.generalForm, this.additionalOptionsForm], this.document);

    if (isValid) {
      const productConfigurationToSave: ProductConfiguration = this.getCurrentProductConfiguration();
      this.saving = true;
      if (this.isEditingProductConfiguration()) {
        this.editProductConfiguration(productConfigurationToSave, saveType);
      } else {
        this.addProductConfiguration(productConfigurationToSave, saveType);
      }
    }
  }

  public duplicate(): void {
    const duplicateUrl = RouteUtils.paths.texEdit.duplicateProductConfiguration.absolutePath.replace(':id', this.getCurrentProductConfiguration().id.toString());
    this.router.navigateByUrl(duplicateUrl);
  }

  public delete(): void {
    const productConfigurationToDelete: ProductConfiguration = this.getCurrentProductConfiguration();
    this.deleteProductConfiguration(productConfigurationToDelete);
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid = Object.values(this.generalForm.controls).some((control: UntypedFormControl) => control.invalid && control.touched);
    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }
    return isFormInvalid && this.saveButtonTouched;
  }

  public canShowGeneralTab(): boolean {
    return this.selectedMenuItem.value === AddProductConfigurationViewMode.GENERAL;
  }

  public canShowOptionalSettingsTab(): boolean {
    return this.selectedMenuItem.value === AddProductConfigurationViewMode.OPTIONAL_SETTINGS;
  }

  public canShowVersionHistoryTab(): boolean {
    return this.selectedMenuItem.value === AddProductConfigurationViewMode.VERSION_HISTORY && this.isEditingProductConfiguration();
  }

  public onSelectedMenuItemChange(menuItem: TabsMenuItem): void {
    this.selectedMenuItem = menuItem;

    if (!this.canShowVersionHistoryTab()) {
      this.closeSelectedVersionHistoryDetails();
    }
  }

  public canShowVersionHistoryDetails(): boolean {
    return !isNil(this.selectedVersionHistory);
  }

  public getButtonTitle(): string {
    return this.selectedVersionHistory.readyForProduction ? 'PRODUCTION_ORDER_LITE.VERSION_HISTORY.UNSET_READY_FOR_PRODUCTION' : 'PRODUCTION_ORDER_LITE.VERSION_HISTORY.SET_READY_FOR_PRODUCTION';
  }

  public updateReadyForProduction(): void {
    this.productConfigurations
      .updateReadyForProduction(Number(this.activatedRoute.snapshot.params.id), Number(this.selectedVersionHistory.version))
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe({
        next: () => {
          this.updateVersionHistories();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.editObjectTitle, errorMessage.message)
      });
  }

  public updateAdditionalInformation(): void {
    this.productConfigurations
      .updateAdditionalInformation(this.activatedRoute.snapshot.params.id, this.selectedVersionHistory.version, this.additionalInformation.value)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe({
        next: () => {
          this.updateVersionHistories();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.editObjectTitle, errorMessage.message)
      });
  }

  public closeSelectedVersionHistoryDetails(): void {
    this.selectedVersionHistory = null;
    this.versionsHistoryGrid?.api?.deselectAll();
  }

  public isEditingProductConfiguration(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === this.EDIT_PRODUCT_CONFIGURATION_URL;
  }

  public addProductionScheduleForProductConfiguration(): void {
    this.router.navigateByUrl(RouteUtils.paths.texFab.productionOrder.addProductionOrderForProductConfiguration.absolutePath.replace(':id', this.activatedRoute.snapshot.params.id));
  }

  public canExecuteActions(): boolean {
    return !this.productConfigurationLocked && !this.productConfigurationUnsavedChanges;
  }

  public canShowSuggestionForProductConfigurationName(): boolean {
    return this.suggestionForProductConfigurationName?.length > 0 && this.generalForm.getRawValue().name !== this.suggestionForProductConfigurationName;
  }

  public applySuggestionForProductConfigurationName(): void {
    this.generalForm.controls.name.setValue(this.suggestionForProductConfigurationName);
  }

  private getSuggestionForProductConfigurationName(): void {
    if (!this.productConfigurationLocked && this.getPropertyValue('machine') && this.getPropertyValue('quality')) {
      this.productConfigurations
        .generateName(this.getCurrentProductConfiguration())
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((generatedName: string) => {
          this.suggestionForProductConfigurationName = generatedName;
        });
    }
  }

  private initialiseProductConfiguration(productConfiguration: ProductConfiguration): void {
    this.productConfigurationLocked = productConfiguration.locked;
    this.selectedDesigns = productConfiguration.designs;
    this.initialiseDesignGridOptions();
    const selectionOptions = {
      enableClickSelection: !this.productConfigurationLocked,
      checkboxes: !this.productConfigurationLocked,
      mode: AgGridRowSelection.MULTIPLE,
      headerCheckbox: !this.productConfigurationLocked
    } as RowSelectionOptions;
    this.gridOptionsForListOfDesigns.rowSelection = selectionOptions;
    this.gridOptionsForListOfSelectedDesigns.rowSelection = selectionOptions;
    this.setFormValues(productConfiguration);
    this.customMessageTranslationKeys = {main: this.translate.instant('PRODUCT_CONFIGURATION.OPEN_IN_CADCAM', {object: productConfiguration.name}), sub: 'PRODUCT_CONFIGURATION.WARNING'};
    this.observeFormChanges();

    if (this.isEditingProductConfiguration() && this.currentSubscription?.hasPermission(Permission.TEXFAB_SCHEDULE)) {
      this.menuItems.push({value: AddProductConfigurationViewMode.VERSION_HISTORY, translationKey: 'PRODUCTION_ORDER_LITE.VERSION_HISTORY.VERSION_HISTORY'});
      this.listOfVersionHistories = productConfiguration.versionHistories;
      this.initialiseVersionHistoryGridOptions();
    }
  }

  private initialiseDesignGridOptions(): void {
    this.gridOptionsForListOfDesigns = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfDesigns(), GridIdentifier.PRODUCT_CONFIGURATION_DESIGNS_LIST)
      .withHeaderHeight(64)
      .withRowHeight(96)
      .withRowSelection(true, false)
      .withLoadingCellRenderer('customLoadingCellRenderer')
      .withComponents({
        customLoadingCellRenderer: LoadingCellOverlayComponent
      })
      .withServerSideDataSource(this.QUANTITY_OF_DRAWINGS_TO_REQUEST_PER_SLICE, this.getServerSideDatasource())
      .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelected(event))
      .withNoRowsOverlay(this.getNoRowsOverlayTitleParameters(false))
      .build();

    this.gridOptionsForListOfSelectedDesigns = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfDesigns(), GridIdentifier.PRODUCT_CONFIGURATION_DESIGNS_LIST)
      .withHeaderHeight(64)
      .withRowHeight(96)
      .withRowSelection(true, false)
      .withOnGridReady(({api}: GridReadyEvent) => this.setSelectedDesigns(api))
      .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelected(event))
      .withNoRowsOverlay(this.getNoRowsOverlayTitleParameters(true))
      .build();
  }

  private initialiseVersionHistoryGridOptions(): void {
    this.gridOptionsForListOfVersionHistories = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefsForListOfVersionHistories(), GridIdentifier.VERSION_HISTORIES_LIST)
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: this.translationKeyForProductConfiguration,
        hideDescription: true
      } as NoDataOverlayComponentParams)
      .withOnRowClicked((event: RowClickedEvent) => this.onRowClicked(event))
      .withGetRowId((params: GetRowIdParams): string => params.data.version)
      .build();
  }

  private getColumnDefsForListOfDesigns(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withValueGetter((params: ValueGetterParams<any>) => params.data)
        .withHeaderName('GENERAL.PREVIEW')
        .withCellRenderer(DesignPreviewComponent)
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('name', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellClass(() => (this.productConfigurationLocked ? ['cell-disabled'] : []))
        .withLockVisible()
        .withTextMultiFilter(this.getPossibleColumnsValues('name'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('widthInPx')
        .withField('dimensionsInPixels.widthInPx')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH')
        .withRightAlignment()
        .withComparator(intComparator)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), Unit.PIXEL, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PIXEL, this.l10nIntlService))
        .withNumericMultiFilter(this.getPossibleColumnsValues('widthInPx'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('heightInPx')
        .withField('dimensionsInPixels.heightInPx')
        .withHeaderName('GENERAL.DIMENSIONS.LENGTH')
        .withRightAlignment()
        .withComparator(intComparator)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), Unit.PIXEL, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PIXEL, this.l10nIntlService))
        .withNumericMultiFilter(this.getPossibleColumnsValues('heightInPx'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('shape')
        .withHeaderName('GENERAL.SHAPE')
        .withComparator(StringUtils.stringComparator)
        .withValueFormatter((params: ValueFormatterParams) => FixedShape.getTranslationKeyForShape(params.value, this.translate))
        .withTooltipValueGetter((params: ITooltipParams) => FixedShape.getTranslationKeyForShape(params.value, this.translate))
        .withTextMultiFilter(this.getPossibleColumnsValues('shape'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('nrColumnsForWeftSelection', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.NR_SEL_COL')
        .withRightAlignment()
        .withNumericMultiFilter(this.getPossibleColumnsValues('nrColumnsForWeftSelection'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('dateModified')
        .withHeaderName('TEXTILE_DATA.LAST_MODIFIED')
        .withDate()
        .withDateMultiFilter(this.getPossibleColumnsValues('dateModified'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('sizeInKB')
        .withHeaderName('DESIGN_LIBRARY.DETAILS.SIZE')
        .withRightAlignment()
        .withValueGetter((params: ValueGetterParams) => Math.round(params.data.sizeInKB))
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), Unit.KILOBYTE, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.KILOBYTE, this.l10nIntlService))
        .withoutFilter()
        .withSortable()
        .build()
    ];
  }

  private getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        const sortModel = params.request.sortModel;
        const startRow = params.request.startRow;
        const endRow = params.request.endRow;
        const filterModel = params.request.filterModel;

        const gridModel = new GridModel(startRow, endRow, sortModel, filterModel);

        this.drawings
          .getAllWithoutImageDataFromGridModel(TargetForListOfDrawingsEnum.PRODUCT_CONFIGURATION, gridModel, false, `${DrawingType.TFT.toLowerCase()},${DrawingType.EP.toLowerCase()}`)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe({
            next: (drawings: Drawing[]) => {
              let endRowForDrawingLibrary: number;

              if (drawings.length < this.QUANTITY_OF_DRAWINGS_TO_REQUEST_PER_SLICE) {
                endRowForDrawingLibrary = startRow + drawings.length;
              }

              if (startRow === 0) {
                if (drawings.length === 0) {
                  params.api.showNoRowsOverlay();
                } else {
                  params.api.hideOverlay();
                }
              }

              this.loadingForTheFirstTime = false;

              this.gridOptionsForListOfDesigns.loadingCellRendererParams = {
                loadingForTheFirstTime: this.loadingForTheFirstTime
              };

              params.success({
                rowData: drawings,
                rowCount: endRowForDrawingLibrary
              });

              this.setSelectedDesigns(params.api);
            },
            error: () => {
              params.success({
                rowData: null,
                rowCount: 0
              });
              params.api.showNoRowsOverlay();
            }
          });
      }
    };
  }

  private setSelectedDesigns(gridApi: GridApi): void {
    if (this.selectedDesigns?.length > 0) {
      gridApi.forEachNode((node: RowNode) => {
        node.setSelected(this.selectedDesigns.some((design: Drawing) => design.id === node.data.id));
      });
    }
  }

  private getColumnDefsForListOfVersionHistories(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withField('dateModified').withHeaderName('PRODUCTION_ORDER_LITE.VERSION_HISTORY.DATE_TIME').withDate().withSortable(false).build(),
      this.colDefBuilderFactoryService.getBuilder().withField('version').withHeaderName('GENERAL.VERSION').withRightAlignment().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withValueGetter((params: ValueGetterParams<VersionHistory>) => {
          return `${params.data.qualityName} (v${params.data.qualityVersion})`;
        })
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_STRUCTURE')
        .withValueGetter((params: ValueGetterParams<VersionHistory>) => {
          return `${params.data.weaveStructureName} (v${params.data.weaveStructureVersion})`;
        })
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withField('additionalInformation').withHeaderName('PRODUCTION_ORDER_LITE.VERSION_HISTORY.ADDITIONAL_INFORMATION').build(),
      this.colDefBuilderFactoryService.getBuilder().withField('readyForProduction').withHeaderName('GENERAL.STATUS').withCellRenderer(GridProductionStatusComponent).withSortable(false).build()
    ];
  }

  private onRowSelected(event: RowSelectedEvent): void {
    const selectedDesign = event.node.data;
    if (event.node.isSelected()) {
      if (!this.selectedDesigns.some((design: Drawing) => design.id === selectedDesign.id)) {
        this.selectedDesigns.push(selectedDesign);
      }
    } else {
      this.selectedDesigns = this.selectedDesigns.filter((design: Drawing) => design.id !== selectedDesign.id);
    }
  }

  private onRowClicked(event: RowClickedEvent): void {
    if (event && event.node && event.node.data) {
      const selectedVersionHistory = event.node.data;
      if (event.node.isSelected() && this.selectedVersionHistory !== selectedVersionHistory) {
        this.selectedVersionHistory = selectedVersionHistory;
        this.additionalInformation.setValue(this.selectedVersionHistory.additionalInformation);
      } else {
        this.selectedVersionHistory = null;
        event.api.deselectAll();
      }
    }
  }

  private replaceUrlToEditCreatedProductConfiguration(id: string): void {
    this.router.navigate([RouteUtils.paths.texEdit.editProductConfiguration.absolutePath.replace(':id', id)]);
  }

  private addProductConfiguration(productConfiguration: ProductConfiguration, saveType: SaveType): void {
    this.productConfigurations
      .save(productConfiguration)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe({
        next: (id: number) => {
          this.showToast('GENERAL.ACTIONS.CREATED_OBJECT', productConfiguration.name);

          if (saveType === SaveType.SAVE_AND_CLOSE) {
            this.navigateBack();
          } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
            this.addProductConfigurationForm?.reset();
          } else if (this.navigationHelperService.hasPreviousRoute()) {
            this.navigationHelperService.savePartialState<NavigationNewItemData>({newItemId: id});
            this.navigateBack();
          } else {
            this.replaceUrlToEditCreatedProductConfiguration(`${id}`);
          }
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError('GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
  }

  private editProductConfiguration(productConfiguration: ProductConfiguration, saveType: SaveType): void {
    this.productConfigurations
      .update(productConfiguration)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe({
        next: () => {
          this.showToast('GENERAL.ACTIONS.EDITED_OBJECT', productConfiguration.name);
          this.productConfigurationUnsavedChanges = false;

          if (saveType === SaveType.SAVE_AND_CLOSE) {
            this.navigateBack();
          } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
            this.router.navigateByUrl(LastModifiedCardUtils.getPageUrls('productConfiguration').add);
          }
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.editObjectTitle, errorMessage.message)
      });
  }

  private deleteProductConfiguration(productConfiguration: ProductConfiguration): void {
    this.productConfigurations
      .delete(productConfiguration.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe({
        next: () => {
          this.showToast('GENERAL.ACTIONS.DELETED_OBJECT', productConfiguration.name);
          this.productConfigurationUnsavedChanges = false;
          this.navigateBack();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.editObjectTitle, errorMessage.message)
      });
  }

  private getExternalApplicationLink(): Observable<string> {
    return this.profile
      .getExternalApplicationKey()
      .pipe(
        map(
          (externalApplicationKey: string) =>
            `txcccm://application_key:${externalApplicationKey}:${btoa(
              `{"cfgVersion":1, "context":"editProduct", "projectId": 0, "productId": ${this.getCurrentProductConfiguration().id}, "epId":0}`
            )}`
        )
      );
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.translationKeyForProductConfiguration, {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private showToast(action: string, productConfigurationName: string): void {
    this.toastService.showInfo({
      tapToDismiss: false,
      timeOut: 2000,
      message: this.translate.instant(action, {
        object: this.translate.instant(this.translationKeyForProductConfiguration, {count: 1}),
        name: productConfigurationName,
        count: 1
      })
    });
  }

  private getCurrentProductConfiguration(): ProductConfiguration {
    const name: string = this.getPropertyValue('name');
    const machine: MachineSummary = this.getPropertyValue('machine');
    const quality: WeaveQuality = this.getPropertyValue('quality');
    const helpWeaveStructures: WeaveStructure[] = this.additionalOptionsForm?.value.helpWeaveStructures;
    const weftColoredYarnSet: OverviewListColoredYarnSet = this.additionalOptionsForm?.value.weftColoredYarnSet;
    const coloredYarnSet: OverviewListColoredYarnSet = this.additionalOptionsForm?.value.coloredYarnSet;
    const description: string = this.additionalOptionsForm?.value.description;

    return new ProductConfiguration(
      this.isEditingProductConfiguration() ? this.activatedRoute.snapshot.params.id : null,
      name,
      machine,
      quality,
      helpWeaveStructures,
      this.selectedDesigns,
      coloredYarnSet,
      weftColoredYarnSet,
      this.listOfVersionHistories,
      this.productConfigurationLocked,
      description
    );
  }

  private setProductConfigurationForm(): void {
    const generalForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required, AsyncUniqueValidator.createValidator(this.productConfigurations, null)),
      machine: this.formBuilder.control({value: null, disabled: this.productConfigurationLocked}, Validators.required),
      quality: this.formBuilder.control({value: null, disabled: this.productConfigurationLocked}, Validators.required)
    });

    const additionalOptionsForm = this.formBuilder.group({
      helpWeaveStructures: this.formBuilder.control(null),
      weftColoredYarnSet: this.formBuilder.control(null),
      coloredYarnSet: this.formBuilder.control(null),
      description: this.formBuilder.control(null)
    });

    this.addProductConfigurationForm = this.formBuilder.group({
      generalForm,
      additionalOptionsForm
    });

    this.generalMenuItem.form = this.generalForm;
    this.optionalSettingsMenuItem.form = this.additionalOptionsForm;
  }

  private setFormValues(productConfiguration: ProductConfiguration): void {
    const name = this.isEditingProductConfiguration() ? productConfiguration.name : null;

    this.generalForm.patchValue({
      name: name,
      machine: productConfiguration.machine,
      quality: productConfiguration.quality
    });
    if (this.productConfigurationLocked) {
      this.generalForm.controls.name.disable();
    }

    this.getSuggestionForProductConfigurationName();
    this.generalForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.productConfigurations, productConfiguration.name)]);
    this.generalForm.controls.name.updateValueAndValidity();

    this.additionalOptionsForm.patchValue({
      helpWeaveStructures: productConfiguration?.helpWeaveStructures,
      weftColoredYarnSet: productConfiguration?.weftColoredYarnSet,
      coloredYarnSet: productConfiguration?.coloredYarnSet,
      description: productConfiguration?.description
    });

    this.navigateToPreviousSelectedMenuItem();
  }

  private navigateToPreviousSelectedMenuItem(): void {
    const emptySelectedMenuItemState = {menuItem: null} as NavigationSelectedMenuItemData;
    const selectedMenuItemState = this.navigationHelperService.getPartialState<NavigationSelectedMenuItemData>(Object.keys(emptySelectedMenuItemState));
    if (selectedMenuItemState && !ObjectUtils.isDeepEqual(emptySelectedMenuItemState, selectedMenuItemState)) {
      this.selectedMenuItem = selectedMenuItemState.menuItem;
    }
  }

  private setNewQuality(id: number): void {
    const parameters: Partial<GetAllMachineQualitiesParameters> = {showOnlyLatestVersion: true};
    this.qualities
      .getAll(parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((qualities: OverviewListMachineQuality[]) => this.setQuality(qualities.find((quality: OverviewListMachineQuality) => quality.id === id)));
  }

  private setQuality(quality: OverviewListMachineQuality): void {
    this.generalForm.controls.quality.setValue(quality);
    this.getSuggestionForProductConfigurationName();
    this.removeFromHelpWeaveStructures(quality.singleWidth?.weaveStructure);
  }

  private setNewColoredYarnSet(id: number): void {
    this.coloredYarnSets
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfColoredYarnSets: OverviewListColoredYarnSet[]) => {
        const newlyCreatedColoredYarnSet: OverviewListColoredYarnSet = listOfColoredYarnSets.find((coloredYarnSet: OverviewListColoredYarnSet) => coloredYarnSet.id === id);

        this.additionalOptionsForm.controls.coloredYarnSet.setValue(newlyCreatedColoredYarnSet);
      });
  }

  private setNewWeftColoredYarnSet(id: number): void {
    this.weftColoredYarnSets
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfWeftColoredYarnSets: OverviewListColoredYarnSet[]) => {
        const newlyCreatedWeftColoredYarnSet = listOfWeftColoredYarnSets.find((weftColoredYarnSet: OverviewListColoredYarnSet) => weftColoredYarnSet.id === id);

        this.additionalOptionsForm.controls.weftColoredYarnSet.setValue(newlyCreatedWeftColoredYarnSet);
      });
  }

  private getPropertyValue(propertyName: string): any {
    return this.generalForm?.get(propertyName)?.value;
  }

  private observeFormChanges(): void {
    this.addProductConfigurationForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.productConfigurationUnsavedChanges = true;
    });
  }

  private updateVersionHistories(): void {
    this.showToast(this.isEditingProductConfiguration() ? 'GENERAL.ACTIONS.EDITED_OBJECT' : 'GENERAL.ACTIONS.CREATED_OBJECT', this.getPropertyValue('name'));
    this.closeSelectedVersionHistoryDetails();

    this.productConfigurations
      .getById(Number(this.activatedRoute.snapshot.params.id))
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: (productConfiguration: ProductConfiguration) => {
          this.listOfVersionHistories = productConfiguration.versionHistories;
        },
        error: ErrorHandlers.navigateToOverviewAndThrowError(this.router, this.PRODUCT_CONFIGURATION_OVERVIEW_URL)
      });
  }

  private getNoRowsOverlayTitleParameters(forSelected: boolean): Partial<NoDataOverlayComponentParams> {
    const parameters: Partial<NoDataOverlayComponentParams> = {
      scale: 1,
      titleKey: null,
      titleParam: null,
      hideDescription: true,
      isAnyFilterPresent: () => {
        return ((this.showSelected && this.selectedDesigns.length > 0) || !this.showSelected) && this.getGridApi()?.isAnyFilterPresent();
      }
    };

    if (forSelected) {
      parameters.titleKey = 'PRODUCTION_ORDER.OVERLAY.NO_OBJECTS_SELECTED_YET';
      parameters.titleParam = {objects: this.translate.instant('DESIGN_LIBRARY.DESIGN', {count: 2}).toLowerCase()};
    } else {
      parameters.titleParam = this.translate.instant('DESIGN_LIBRARY.DESIGN', {count: 1});
    }

    return parameters;
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.drawings.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private getGridApi(): GridApi {
    return this.showSelected ? this.selectedDesignsGrid?.api : this.designsGrid?.api;
  }

  private setMachine(): void {
    const emptyMachineOverviewState = {equipmentId: null} as NavigationMachineOverviewData;
    const machineOverviewState = this.navigationHelperService.getPartialState<NavigationMachineOverviewData>(Object.keys(emptyMachineOverviewState), true);

    if (!AssertionUtils.isNullOrUndefined(machineOverviewState) && !ObjectUtils.isDeepEqual(machineOverviewState, emptyMachineOverviewState)) {
      this.machines
        .getAll()
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((listOfMachines: MachineOverviewList[]) => {
          this.generalForm.controls.machine.setValue(listOfMachines.find((machine: MachineOverviewList) => machine.id === machineOverviewState.equipmentId));
          this.generalForm.controls.machine.disable();
          this.disableMachine = true;
        });
    }
  }
}
